import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import PlayVoice from "@/components/routine/PlayVoice";

const localDate = () => new Date().toLocaleDateString("en-CA");

function Ask({ q, value, onPick, testId }) {
  return (
    <div className="mt-4" data-testid={testId}>
      <p className="text-sm leading-snug">{q}</p>
      <div className="flex gap-2 mt-2">
        {[["done", "Done"], ["not_yet", "Not yet"]].map(([v, l]) => (
          <button key={v} onClick={() => onPick(v)} data-testid={`${testId}-${v}`}
            className={`rounded-full px-4 py-2 text-sm border transition-colors ${value === v ? "bg-primary text-primary-foreground border-primary" : "border-white/15 hover:border-primary/50"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

// Morning: his coach asks about last night's audio and yesterday's action. Not done → it carries over to tonight.
export default function MorningCheckIn() {
  const [c, setC] = useState(null);
  const [audio, setAudio] = useState(null);
  const [action, setAction] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/routine/morning", { params: { local_date: localDate() } }).then((r) => setC(r.data.checkin)).catch(() => {}); }, []);
  const audioAns = c?.audio_done ? "done" : audio;
  useEffect(() => {
    if (!c || !audioAns || !action || res || busy) return;
    setBusy(true);
    api.post(`/routine/morning/${c.id}`, { audio: audioAns, action, local_date: localDate() })
      .then((r) => { setRes(r.data); window.dispatchEvent(new Event("xc-tonight-refresh")); })
      .catch(() => {}).finally(() => setBusy(false));
  }, [c, audioAns, action, res, busy]);
  if (!c) return null;
  return (
    <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8 rounded-3xl border border-amber-200/25 bg-amber-200/[0.04] p-5 sm:p-6" data-testid="morning-checkin">
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-200 flex items-center gap-2"><Sun className="w-3.5 h-3.5" /> Good morning · {c.coach}</p>
      <AnimatePresence mode="wait">
        {res ? (
          <motion.div key="res" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-start gap-3 mt-3" data-testid="morning-checkin-result">
            {res.audio && <PlayVoice label={`Hear ${c.coach}`} testId="morning-checkin-play" load={async () => res.audio} />}
            <p className="font-serif text-lg leading-snug" data-testid="morning-checkin-line">{res.line}</p>
          </motion.div>
        ) : (
          <motion.div key="ask" exit={{ opacity: 0 }}>
            <p className="font-serif text-xl leading-snug mt-2">How did last night go?</p>
            {c.audio_done
              ? <p className="mt-4 text-sm flex items-center gap-2" data-testid="morning-checkin-audio-done"><Check className="w-4 h-4 text-emerald-400" /> You did {c.audio.title} before you slept.</p>
              : <Ask q={`Did you do ${c.audio.title} before you slept?`} value={audio} onPick={setAudio} testId="morning-checkin-audio" />}
            <Ask q={`And yesterday's action: “${c.action}”`} value={action} onPick={setAction} testId="morning-checkin-action" />
            {busy && <p className="mt-3 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {c.coach} is listening…</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
